import React, { useEffect, useState, useContext, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { LanguageContext } from '../../contexts/LanguageContext';
import { getLanguageConfig } from '../../config/languages';
import { scrollToSection } from '../../utils/scrollUtils';
import LanguageDropdown from '../ui/LanguageDropdown';
import styles from './Header.module.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero-section');
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        setIsHeroVisible(rect.bottom > 0);
      } else {
        setIsHeroVisible(false);
      }
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInitialLoad]);

  const handleNavClick = (event, item) => {
    if (isHomePage && !item.href) {
      event.preventDefault();
      scrollToSection(item.id);
    }
    setIsMenuOpen(false);
  };

  const useLightHeader = isHomePage && isHeroVisible && !isMenuOpen;
  const headerClass = `${styles.header} ${
    !isHomePage || ((!isHeroVisible || isMenuOpen) && !isInitialLoad)
      ? styles.headerScrolled
      : ''
  }`;

  const linkStyle = {
    color: useLightHeader ? 'white' : 'hsl(var(--foreground))',
    transition: 'color 0.3s ease-in-out',
  };

  // 動画の上では白ロゴ、それ以外は通常ロゴを表示する。
  const getLogoPath = () => {
    // ヒーロー表示中でメニュー閉じ → 白ロゴ
    const useWhiteLogo = useLightHeader;
    const suffix = useWhiteLogo ? 'white' : 'normal';
    return `${process.env.PUBLIC_URL}/assets/images/${currentLanguageConfig.logo}_jtc_logo_${suffix}.svg`;
  };

  const currentLanguageConfig = getLanguageConfig(locale);

  const navItems = [
    { id: 'about', title: intl.formatMessage({ id: 'header.about' }) },
    { id: 'service', title: intl.formatMessage({ id: 'header.service' }) },
    {
      id: 'news',
      title: intl.formatMessage({ id: 'news.subtitle' }),
      href: '/news/',
    },
    // 言語設定に基づいて採用情報の表示を制御
    ...(currentLanguageConfig.showRecruit
      ? [{ id: 'recruit', title: intl.formatMessage({ id: 'header.recruit' }) }]
      : []),
    { id: 'company', title: intl.formatMessage({ id: 'header.company' }) },
    { id: 'contact', title: intl.formatMessage({ id: 'header.contact' }) },
  ];

  const iconColor = useLightHeader ? '#ffffff' : 'hsl(var(--foreground))';
  const textColor = useLightHeader ? '#ffffff' : 'hsl(var(--foreground))';

  return (
    <header className={headerClass}>
      <div
        className={`${styles.container} 2xl:max-w-[95%] 3xl:max-w-[90%] 2xl:px-8 3xl:px-12`}
      >
        <button
          onClick={() => {
            if (isHomePage) {
              window.scrollTo(0, 0);
            } else {
              navigate('/');
            }
          }}
          className={styles.logo}
        >
          <div className={`${styles.logoWrapper} h-full`}>
            <img
              src={getLogoPath()}
              alt={intl.formatMessage({ id: 'header.logoAlt' })}
              className={`${styles.logoImage} h-full w-auto object-contain object-left`}
            />
          </div>
        </button>
        <nav className={`${styles.nav} 2xl:space-x-4 3xl:space-x-6`}>
          {navItems.map(item => (
            <a
              key={item.id}
              href={item.href || (isHomePage ? `#${item.id}` : `/#${item.id}`)}
              className={`${styles.navLink} ${locale === 'ja' ? 'font-body text-ja-sm' : 'font-heading'} 2xl:text-lg 3xl:text-xl`}
              style={linkStyle}
              onClick={e => handleNavClick(e, item)}
            >
              {item.title}
            </a>
          ))}
          {/* デスクトップ用言語切り替えプルダウン */}
          <LanguageDropdown
            iconColor={iconColor}
            textColor={textColor}
            className={`${locale === 'ja' ? 'font-body text-ja-sm' : 'font-heading'} 2xl:text-lg 3xl:text-xl`}
          />
        </nav>
        <div className={styles.mobileButtons}>
          {/* モバイル用言語切り替えボタン */}
          <div
            className={`${styles.mobileButton} ${useLightHeader ? styles.mobileButtonLight : ''}`}
          >
            <LanguageDropdown
              iconColor={iconColor}
              textColor={textColor}
              className="flex flex-col items-center"
              isMobile={true}
            />
          </div>
          {/* モバイル用メニューボタン */}
          <button
            className={`${styles.mobileButton} ${useLightHeader ? styles.mobileButtonLight : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            ref={menuButtonRef}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMenuOpen ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={iconColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="text-xs">CLOSE</span>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={iconColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
                <span className="text-xs">MENU</span>
              </>
            )}
          </button>
        </div>
      </div>
      {/* モバイルメニュー */}
      <div
        id="mobile-navigation"
        inert={!isMenuOpen}
        className={`${styles.mobileMenu} ${isMenuOpen ? styles.mobileMenuOpen : ''}`}
      >
        {navItems.map(item => (
          <a
            key={item.id}
            href={item.href || (isHomePage ? `#${item.id}` : `/#${item.id}`)}
            className={`${styles.mobileNavLink} ${locale === 'ja' ? 'font-body text-ja-sm' : 'font-heading'}`}
            onClick={e => handleNavClick(e, item)}
          >
            <span className={styles.mobileNavTitle}>{item.title}</span>
          </a>
        ))}
      </div>
    </header>
  );
};

export default Header;
